import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { FiHome, FiMail, FiLock, FiArrowRight } from 'react-icons/fi';

export default function AuthPage() {
  const { signIn, signUp, signInWithMagicLink } = useAuth();
  const [mode, setMode] = useState('signin'); // signin | signup | magic
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setSubmitting(true);

    try {
      if (mode === 'magic') {
        const { error } = await signInWithMagicLink(email);
        if (error) throw error;
        setMessage('Check your email for a login link.');
      } else if (mode === 'signup') {
        const { error } = await signUp(email, password);
        if (error) throw error;
        setMessage('Account created! Check your email to confirm, then sign in.');
      } else {
        const { error } = await signIn(email, password);
        if (error) throw error;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/logo.png" onError={e => { e.target.onerror = null; e.target.src = '/logo.svg'; }} alt="PropManagePro" className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900">PropManagePro</h1>
          <p className="text-gray-500 mt-1">Property Management Dashboard</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">
            {mode === 'signin' && 'Welcome back'}
            {mode === 'signup' && 'Create your account'}
            {mode === 'magic' && 'Sign in with email link'}
          </h2>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {message && (
            <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
              <div className="relative">
                <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all text-sm"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            {mode !== 'magic' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition-all text-sm"
                    placeholder="Your password"
                    required
                    minLength={6}
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  {mode === 'signin' && 'Sign In'}
                  {mode === 'signup' && 'Create Account'}
                  {mode === 'magic' && 'Send Login Link'}
                  <FiArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          {/* Mode switchers */}
          <div className="mt-6 pt-6 border-t border-gray-100 space-y-2 text-center text-sm">
            {mode === 'signin' && (
              <>
                <p className="text-gray-500">
                  No account?{' '}
                  <button onClick={() => { setMode('signup'); setError(''); setMessage(''); }} className="text-indigo-600 hover:text-indigo-700 font-medium">
                    Sign up
                  </button>
                </p>
                <p className="text-gray-500">
                  Or{' '}
                  <button onClick={() => { setMode('magic'); setError(''); setMessage(''); }} className="text-indigo-600 hover:text-indigo-700 font-medium">
                    use a magic link
                  </button>
                </p>
              </>
            )}
            {mode === 'signup' && (
              <p className="text-gray-500">
                Already have an account?{' '}
                <button onClick={() => { setMode('signin'); setError(''); setMessage(''); }} className="text-indigo-600 hover:text-indigo-700 font-medium">
                  Sign in
                </button>
              </p>
            )}
            {mode === 'magic' && (
              <p className="text-gray-500">
                Back to{' '}
                <button onClick={() => { setMode('signin'); setError(''); setMessage(''); }} className="text-indigo-600 hover:text-indigo-700 font-medium">
                  password sign in
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
